import {
  AlertTriangle,
  FileText,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { UrgencyBadge } from '@/components/shared/UrgencyBadge';
import { FrameworkBadge } from '@/components/shared/FrameworkBadge';
import type { PublishQueueEntry } from '@/types/api';
import { cn } from '@/lib/utils';

interface PublishQueueEntryCardProps {
  readonly entry: PublishQueueEntry;
  readonly isSelected: boolean;
  readonly onToggleSelect: (id: string) => void;
}

function formatDepartment(dept: string): string {
  return dept.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const STATUS_BADGE: Record<
  PublishQueueEntry['status'],
  { label: string; className: string; spinning?: boolean }
> = {
  approved: { label: 'Approved', className: 'bg-teal/15 text-teal' },
  queued: { label: 'Queued', className: 'bg-blue-500/15 text-blue-400' },
  publishing: { label: 'Publishing', className: 'bg-amber-500/15 text-amber-400', spinning: true },
  published: { label: 'Published', className: 'bg-green-500/15 text-green-400' },
  failed: { label: 'Failed', className: 'bg-red-500/15 text-red-400' },
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function PublishQueueEntryCard({
  entry,
  isSelected,
  onToggleSelect,
}: PublishQueueEntryCardProps) {
  const badge = STATUS_BADGE[entry.status];

  return (
    <Card className="transition-colors hover:bg-muted/30">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Checkbox */}
          <div className="pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(entry.entryId)}
            />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1 space-y-2">
            {/* Row 1: Title + Urgency */}
            <div className="flex items-center gap-3">
              <p className="truncate text-base font-semibold text-foreground">
                {entry.documentTitle}
              </p>
              <UrgencyBadge score={entry.urgencyScore} className="shrink-0" />
            </div>

            {/* Row 2: Department + Frameworks + Connectors */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {formatDepartment(entry.department)}
              </span>
              {(entry.frameworksAffected ?? []).slice(0, 2).map((fw) => (
                <FrameworkBadge key={fw} framework={fw} />
              ))}
              {(entry.frameworksAffected?.length ?? 0) > 2 && (
                <span className="text-xs text-muted-foreground">
                  +{entry.frameworksAffected!.length - 2}
                </span>
              )}
              {(entry.connectorTargets ?? []).map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  <FileText className="h-3 w-3" />
                  {c}
                </span>
              ))}
            </div>

            {/* Row 3: Approved time + by */}
            <p className="text-xs text-muted-foreground">
              Approved {formatRelativeTime(entry.approvedAt)} by{' '}
              {entry.approvedBy}
            </p>

            {/* Dependency warnings */}
            {(entry.dependencyWarnings?.length ?? 0) > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                <p className="text-xs text-amber-300">
                  Depends on unpublished: {entry.dependencyWarnings![0]}
                  {entry.dependencyWarnings!.length > 1 && (
                    <span className="text-amber-400">
                      {' '}+{entry.dependencyWarnings!.length - 1} more
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Status badge */}
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold',
              badge.className,
            )}
          >
            {badge.spinning && (
              <Loader2 className="h-3 w-3 animate-spin" />
            )}
            {badge.label}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export function PublishQueueEntryCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-4 w-4 animate-pulse rounded bg-muted" />
          <div className="flex-1 space-y-3">
            <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
            <div className="flex gap-2">
              <div className="h-5 w-20 animate-pulse rounded bg-muted" />
              <div className="h-5 w-16 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-3 w-32 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-6 w-20 animate-pulse rounded bg-muted" />
        </div>
      </CardContent>
    </Card>
  );
}
